import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ButtonComponent } from '../../button/button.component';
import { debounceTime, fromEvent, startWith } from 'rxjs';
import { IS_SERVER } from '../../../providers/is-platform.provider';
import { CarouselItemComponent } from './carousel-item/carousel-item.component';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-custom-carousel',
  imports: [SvgIconComponent, ButtonComponent, TranslocoModule],
  templateUrl: './custom-carousel.component.html',
  styleUrl: './custom-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomCarouselComponent implements AfterViewInit, OnDestroy {
  private readonly isServer = inject(IS_SERVER);
  private autoplayIntervalId?: NodeJS.Timeout;

  @Input()
  autoplay = false;

  @Input()
  autoplayInterval = 5000;

  @ViewChild('viewport')
  viewport!: ElementRef<HTMLElement>;

  @HostBinding('class')
  @Input()
  appearance: 'default' | 'main-slider' | 'category' | 'collections' | 'syne' = 'default';

  @Output()
  activeItemChange = new EventEmitter<number>();

  children = contentChildren(CarouselItemComponent);
  childrenCount = computed(() => this.children().length);
  color = signal('#414141');
  activeItemIndex = signal(0);
  isStartPosition = computed(() => this.activeItemIndex() === 0);
  isEndPosition = signal(0);
  thumbWidthPercentage = computed(() => 100 / (this.childrenCount() - 1));
  thumbTranslate = computed(() => `${this.activeItemIndex() * 100}%`);

  ngAfterViewInit(): void {
    if (this.isServer) {
      return;
    }

    fromEvent(this.viewport.nativeElement, 'scroll', { passive: true })
      .pipe(debounceTime(50), startWith())
      .subscribe(() => {
        const slideWidth = this.viewport.nativeElement.scrollWidth / this.childrenCount();
        const left = this.viewport.nativeElement.scrollLeft;
        const index = Math.round(left / slideWidth);
        const view = this.viewport.nativeElement;
        const atEnd = Math.abs(view.scrollLeft + view.clientWidth - view.scrollWidth) < 2;
        this.isEndPosition.set(atEnd ? 1 : 0);

        if (index !== this.activeItemIndex()) {
          this.activeItemIndex.set(index);
          this.activeItemChange.emit(this.activeItemIndex());
        }
      });

    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  onNext(): void {
    const step = this.getStepCount();
    this.goTo(this.activeItemIndex() + step);
  }

  onPrev(): void {
    const step = this.getStepCount();
    this.goTo(this.activeItemIndex() - step);
  }

  private getStepCount(): number {
    if (this.appearance !== 'syne') {
      return 1;
    }

    const view = this.viewport.nativeElement;
    const totalWidth = view.scrollWidth;
    const visibleWidth = view.clientWidth;
    const childCount = this.childrenCount();

    if (childCount === 0 || totalWidth === 0 || visibleWidth === 0) {
      return 1;
    }

    const singleSlideWidth = totalWidth / childCount;
    const fullyVisibleSlides = Math.floor(visibleWidth / singleSlideWidth);

    return Math.max(1, fullyVisibleSlides);
  }

  startAutoplay(): void {
    this.stopAutoplay();

    this.autoplayIntervalId = setInterval(() => {
      this.onNext();
    }, this.autoplayInterval);
  }

  stopAutoplay(): void {
    if (this.autoplayIntervalId) {
      clearInterval(this.autoplayIntervalId);
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  goTo(i: number): void {
    if (this.appearance === 'syne') {
      if (i >= this.childrenCount()) {
        i = this.childrenCount() - 1;
      } else if (i < 0) {
        i = 0;
      }

      const view = this.viewport.nativeElement;
      const slideWidth = view.scrollWidth / this.childrenCount();

      view.scrollTo({ left: i * slideWidth, behavior: 'smooth' });

      this.activeItemIndex.set(i);
      this.activeItemChange.emit(i);
    } else {
      if (i >= this.childrenCount()) {
        i = 0;
      } else if (i < 0) {
        i = this.childrenCount() - 1;
      }

      const view = this.viewport.nativeElement;
      const slideWidth = view.scrollWidth / this.childrenCount();

      view.scrollTo({ left: i * slideWidth, behavior: 'smooth' });
    }
  }

  resetScroll(): void {
    const view = this.viewport.nativeElement;
    view.scrollTo({ left: 0, behavior: 'auto' });
    this.activeItemIndex.set(0);
    this.activeItemChange.emit(0);
  }
}
